import { useCallback } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { ApolloError, BaseMutationOptions } from '@apollo/client'
import Hotjar from '@hotjar/browser'
import {
  SubItem,
  SubItems,
} from '@engyalo/delivery-ui-components/lib/interfaces/catalog'
import { useLocation } from 'react-router-dom'
import { IProductCart } from '../../graphQL/mutations/cartRemoveProduct/types'
import useCartRemoveProduct from '../../graphQL/mutations/cartRemoveProduct'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useGetCart } from './useGetCart'
import { onCartResponse } from '../../utils/onCartResponse'
import { noteCartWriteSettled } from '../../utils/settledCartWrites'
import onRemoveProduct from '../../utils/onRemoveProduct'
import { generateDataDogContext } from '../../utils/dataDog'
import { IProduct } from '../../interfaces'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import { addAlert } from '../../redux/slices/config'
import { sendGoogleEvent } from '../../utils/sendGoogleAnalytics'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import formatDataForAnalytics from '../../utils/formatDataForAnalytics'
import { ITrackCartEventPayload } from '../../modules/interfaces'
import YaloAnalytics from '../../modules/analytics'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import { getAnalyticsItem } from '../../utils/getAnalyticsItems'
import {
  hasCartMutationTimedOut,
  reportCartMutationTimeout,
  signalOfMutation,
} from '../../utils/cartMutationTimeout'
import { selectDisableWhileSyncing } from '../../redux/skuSyncing'

export const useDeleteProductFromCart = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const state = useAppSelector((stateRedux) => stateRedux)
  const {
    defaultSlice: { sessionId = '', storeName = '', sessionData },
    cartSlice: cart,
  } = state
  const { config } = state.defaultSlice
  const currency = config?.options?.currencyCode || ''

  // Off by default. See the flag's docblock in interfaces/config.ts for the
  // library behaviour this requires before a storefront turns it on.
  const disableWhileSyncing = useAppSelector(selectDisableWhileSyncing)
  const location = useLocation()

  const getCartAction = useGetCart()

  /*
   * Reads the store when the response lands, not the render-time `state`.
   * Apollo calls this even after the hook's component has unmounted, so the
   * shopper may already be on another page when it runs. The snapshot is then
   * as old as the cart page's last render, and two things go wrong with it:
   * - the category list is `null` in it (`useGetProductsNav` clears it on
   *   mount in every container), so `onCartResponse` skips that list and a
   *   quantity the next page baked from the old cart is never corrected;
   * - its `catalogSlice.categories` is rebuilt and dispatched back, which
   *   replaces a catalogue list loaded after the cart unmounted.
   */
  const onCompleted = (data: IProductCart) => {
    // Counted before the cart is written, success or not, so a refresh
    // whose cart read is out reads it again (COM-1512)
    noteCartWriteSettled()
    if (data.cartRemoveProduct) {
      onCartResponse({
        data: data.cartRemoveProduct,
        state: store.getState(),
        dispatch,
      })
    }
  }

  const onError = (error: ApolloError, clientOptions?: BaseMutationOptions) => {
    noteCartWriteSettled()
    const signal = signalOfMutation(clientOptions)
    const message = getAlertErrorMessage(
      error.networkError || hasCartMutationTimedOut(signal)
        ? AlertMessageType.Network
        : AlertMessageType.RemoveItem,
      config?.texts,
    )
    dispatch(addAlert({ message }))
    getCartAction()
    if (hasCartMutationTimedOut(signal)) {
      reportCartMutationTimeout({
        error,
        sku: clientOptions?.variables?.cartRemoveProductData?.sku,
        operation: 'delete',
        caller: 'useDeleteProductFromCart > onError',
      })
      return
    }
    const context = generateDataDogContext({
      title: 'Remove product mutation error',
      extraInfo: { function: 'useDeleteProductFromCart > onError' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }
  const [removeProduct] = useCartRemoveProduct({
    onError,
    onCompleted,
  })

  const execRemoveCartMutation = (
    quantity: number,
    sku: string,
    product: IProduct,
    currency: string,
    referrer?: Referrers,
    subItems?: SubItems,
  ) => {
    let actionLabel = 'Delete from cart'
    if (referrer) {
      actionLabel = `Delete from cart on ${referrer}`
    }
    const { package: productPackage } = product
    const packageName = productPackage?.name || undefined
    const category = 'cart'
    Hotjar.event(actionLabel)
    sendGoogleEvent(category, actionLabel, sku, quantity)
    const actionName = 'removeFromCart'
    fireGTMEvent('commerce-remove_from_cart', {
      currency,
      value: (product.price || 0) * product.quantity,
      items: [getAnalyticsItem(product)],
    })
    const analyticsData: ITrackCartEventPayload = formatDataForAnalytics(
      location.pathname,
      quantity,
      cart,
      product,
      currency,
      sessionData?.customer?.phoneNumber || [''],
      sessionId,
      actionName,
      storeName,
      sessionData?.extraIds,
    )
    YaloAnalytics.trackCartEvent(analyticsData)
    onRemoveProduct({
      removeProductFunction: removeProduct,
      sku,
      quantity,
      sessionId,
      storeName,
      dispatch,
      replace: true,
      subItems,
      packageName,
      // Only what the timeout needs: nothing here supersedes a delete, and
      // with the flag off the request goes out without a signal, as before.
      controller: disableWhileSyncing
        ? new window.AbortController()
        : undefined,
      disableWhileSyncing,
    })
  }
  const callBackRemoveProductToCart = useCallback(execRemoveCartMutation, [
    dispatch,
    removeProduct,
    sessionId,
    storeName,
    disableWhileSyncing,
  ])

  const removeItemAction = ({
    product,
    referrer,
    currentSubItem,
  }: {
    product: IProduct
    referrer?: Referrers
    currentSubItem?: SubItem
  }) => {
    try {
      const hasSubItems = !!product?.subItems?.length
      // const that contains the subItems to be removed, if it exists
      let subItemsToRemove: SubItems = []
      if (hasSubItems && currentSubItem) {
        const subItems = product?.subItems || []
        const handledSubItems: SubItems = subItems || []
        const indexOfBundle = handledSubItems?.findIndex(
          (item) => item.hash === currentSubItem.hash,
        )
        handledSubItems.splice(indexOfBundle, 1)
        subItemsToRemove = handledSubItems
      }
      const cleanedSubItems = subItemsToRemove.map(
        ({ hash, ...subItemValue }) => subItemValue,
      )
      const handledValue = hasSubItems
        ? cleanedSubItems.reduce((acc, item) => acc + item.quantity, 0)
        : 0
      callBackRemoveProductToCart(
        handledValue,
        product.sku,
        product,
        currency,
        referrer,
        cleanedSubItems,
      )
    } catch (exception) {
      const context = generateDataDogContext({
        title: 'could not delete product from cart',
        extraInfo: { product, function: 'removeItemAction' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
    }
  }
  return removeItemAction
}

export default useDeleteProductFromCart
